const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

const unwrap = (value) => value?.data || value || null

const minutes = (time) => {
  const [hours, minutesValue] = String(time || '').split(':').map(Number)
  return Number.isFinite(hours) && Number.isFinite(minutesValue) ? hours * 60 + minutesValue : null
}

const dateFromMinutes = (year, month, day, value) => {
  const daysToAdd = Math.floor(value / (24 * 60))
  const time = value % (24 * 60)
  return new Date(Date.UTC(year, month - 1, day + daysToAdd, Math.floor(time / 60), time % 60, 0, 0))
}

export const getAssignmentSettingId = (response) => {
  const assignment = unwrap(response)
  return assignment?.work_hour_setting_id || assignment?.workHourSettingId || assignment?.work_hour_setting?.id || assignment?.workHourSetting?.id || null
}

export const getAssignmentSetting = (response) => {
  const assignment = unwrap(response)
  return assignment?.work_hour_setting || assignment?.workHourSetting || null
}

// date_exceptions is stored per-date: [{ date, name, exception_type, time_periods }]
const getDateExceptionRules = (setting, date) => {
  const exception = (setting?.date_exceptions || []).find((item) => item.date === date)
  return (exception?.time_periods?.length ? exception.time_periods : null) || null
}

export const createWorkHourAllocationTimes = (setting, date) => {
  const [year, month, day] = date.split('-').map(Number)
  const weekday = WEEKDAY_NAMES[new Date(Date.UTC(year, month - 1, day)).getUTCDay()]
  const rules = getDateExceptionRules(setting, date) || setting?.weekday_rules?.[weekday] || []
  return rulesToTimes(rules, date)
}

const rulesToTimes = (rules, date) => {
  const [year, month, day] = date.split('-').map(Number)
  const periods = rules
    .map((rule) => ({ start: minutes(rule.start), end: minutes(rule.end) }))
    .filter((rule) => rule.start !== null && rule.end !== null)

  if (!periods.length) return null

  const start = Math.min(...periods.map((rule) => rule.start))
  const end = Math.max(...periods.map((rule) => rule.end <= rule.start ? rule.end + (24 * 60) : rule.end))

  return {
    startTime: dateFromMinutes(year, month, day, start).toISOString(),
    endTime: dateFromMinutes(year, month, day, end).toISOString(),
  }
}

export const naiveUtcISOString = (date, time) => `${date}T${time}:00.000Z`

export const getDefaultStartTime = (setting, date) => {
  const weekday = WEEKDAY_NAMES[new Date(Date.UTC(...date.split('-').map(Number).map((n, i) => i === 1 ? n - 1 : n))).getUTCDay()]
  const rules = getDateExceptionRules(setting, date) || setting?.weekday_rules?.[weekday] || []

  const standardRules = rules.filter((rule) => rule.time_type === 'STANDARD' || !rule.time_type)
  if (!standardRules.length) return null

  const startMinutes = standardRules
    .map((rule) => minutes(rule.start))
    .filter((m) => m !== null)

  if (!startMinutes.length) return null
  const earliest = Math.min(...startMinutes)
  const hours = Math.floor(earliest / 60)
  const mins = earliest % 60
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

export const createChainedAllocationTimes = (setting, date, dailyHours, latestEndTime = null) => {
  const defaultStartTime = getDefaultStartTime(setting, date) || '08:00'
  if (!Number.isFinite(dailyHours) || dailyHours < 0) return null

  const startISO = latestEndTime && latestEndTime.slice(0, 10) === date
    ? latestEndTime
    : naiveUtcISOString(date, defaultStartTime)

  const endISO = new Date(new Date(startISO).getTime() + Math.round(dailyHours * 3600000)).toISOString()
  return { startTime: startISO, endTime: endISO }
}

export const createGlobalAllocationTimes = (globalStartTime, date, dailyHours) => {
  const [year, month, day] = date.split('-').map(Number)
  const startMinutes = minutes(globalStartTime)
  if (startMinutes === null) return null
  if (!Number.isFinite(dailyHours) || dailyHours < 0) return null
  const startTime = dateFromMinutes(year, month, day, startMinutes)
  const endTime = new Date(startTime.getTime() + dailyHours * 60 * 60 * 1000)
  return {
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
  }
}

export const createAllocationTimes = (setting, date, { globalStartTime, dailyHours } = {}) => {
  const exceptionRules = getDateExceptionRules(setting, date)
  if (exceptionRules) return rulesToTimes(exceptionRules, date)

  if (globalStartTime != null && dailyHours != null) {
    const globalTimes = createGlobalAllocationTimes(globalStartTime, date, dailyHours)
    if (globalTimes) return globalTimes
  }

  const fallbackTimes = createWorkHourAllocationTimes(setting, date)
  if (fallbackTimes && Number.isFinite(dailyHours) && dailyHours >= 0) {
    return {
      startTime: fallbackTimes.startTime,
      endTime: new Date(new Date(fallbackTimes.startTime).getTime() + dailyHours * 60 * 60 * 1000).toISOString(),
    }
  }
  return fallbackTimes
}
